import { color, font, layerColor } from '../styles/tokens';
import { getLatestNewsSnapshot } from '../sources/news';
import { registerPanel } from './panelStack';

/**
 * Painel de lista de notícias — canto inferior esquerdo no tablet;
 * bottom sheet full-width no mobile (ver classe `.side-panel` em
 * global.css e panelStack.ts pro mecanismo de um-aberto-por-vez).
 * Não usa EntityRenderer (que desenha markers no mapa); é puro HTML/DOM
 * sobre o `hudRoot`, consistente com hudHeader.ts. Mostra as 5 mais
 * recentes por `observedAt` (= published_at do artigo), com e sem
 * coordenada — diferente do que aparece no mapa (só os artigos com
 * coordenada, via EntityRenderer), esta lista é o superset completo,
 * conforme o requisito de "nem todo artigo tem geolocalização, mas
 * todos entram na lista".
 *
 * Não há um "evento de nova notícia" disparado por news.ts — este
 * painel faz seu próprio polling de leitura (mesmo intervalo do
 * LayerOrchestrator para a camada `news`) chamando
 * `getLatestNewsSnapshot()`, que é síncrono e só lê o que já está em
 * memória (não dispara fetch novo — quem faz fetch é o
 * LayerOrchestrator, via `newsSource.fetchEntities()`).
 *
 * CORREÇÃO (espaçamento — item 1 do diagnóstico de layout): valores de
 * margin/gap/padding inline agora usam a escala nomeada
 * (--space-1..--space-6, ver global.css) em vez de px soltos; o único
 * valor que não batia com nenhum múltiplo de 4 (marginTop do domínio
 * da fonte, 2px) foi arredondado para --space-1 (4px).
 */

const REFRESH_INTERVAL_MS = 15 * 60_000; // mesmo cadence do polling de news no LayerOrchestrator
const MAX_ITEMS = 5;

/** Lê uma variável de espaçamento nomeada direto do :root — usada aqui
 *  porque os elementos deste arquivo são estilizados via inline style,
 *  não classes CSS (mesmo padrão já usado no resto do arquivo). */
function spacing(token: string): string {
  return `var(--space-${token})`;
}

export function mountNewsPanel(hudRoot: HTMLElement): void {
  const panel = document.createElement('div');
  panel.className = 'hud-panel side-panel side-panel--news hud-interactive';

  const title = document.createElement('div');
  title.className = 'label-secondary';
  title.textContent = 'NOTÍCIAS · ATRASO 12H';
  title.style.marginBottom = spacing('2');
  title.style.color = layerColor.news ?? color.textSecondary;

  const list = document.createElement('div');
  list.style.display = 'flex';
  list.style.flexDirection = 'column';
  list.style.gap = spacing('2');

  panel.append(title, list);
  hudRoot.appendChild(panel);
  registerPanel('news', panel, true); // aberto por padrão no tablet; fechado no mobile até o usuário abrir via chip

  function render(): void {
    const articles = getLatestNewsSnapshot()
      .slice()
      .sort((a, b) => new Date(b.observedAt).getTime() - new Date(a.observedAt).getTime())
      .slice(0, MAX_ITEMS);

    list.innerHTML = '';

    if (articles.length === 0) {
      const empty = document.createElement('div');
      empty.textContent = 'Sem notícias no momento.';
      empty.style.color = color.textSecondary;
      empty.style.fontSize = '12px';
      list.appendChild(empty);
      return;
    }

    for (const article of articles) {
      const item = document.createElement('a');
      item.href = article.raw.href;
      item.target = '_blank';
      item.rel = 'noopener noreferrer';
      item.style.display = 'block';
      item.style.textDecoration = 'none';
      item.style.color = color.textPrimary;
      item.style.fontFamily = font.body;
      item.style.fontSize = '13px';
      item.style.lineHeight = '1.4';
      item.style.borderLeft = `2px solid ${layerColor.news ?? color.textSecondary}`;
      item.style.paddingLeft = spacing('2');
      item.textContent = article.label;

      if (article.raw.sourceDomain) {
        const domain = document.createElement('div');
        domain.className = 'mono';
        domain.textContent = article.raw.sourceDomain;
        domain.style.fontSize = '11px';
        domain.style.color = color.textSecondary;
        domain.style.marginTop = spacing('1'); // arredondado de 2px (não batia com a escala de 4px) para 4px
        item.appendChild(domain);
      }

      list.appendChild(item);
    }
  }

  render();
  setInterval(render, REFRESH_INTERVAL_MS);
}
